import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  OnDestroy,
} from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import lightGallery from 'lightgallery';
import lgThumbnail from 'lightgallery/plugins/thumbnail';
import lgZoom from 'lightgallery/plugins/zoom';


@Component({
  selector: 'app-hotel-room',
  standalone: true,
  imports: [CommonModule, MatTooltipModule],
  templateUrl: './hotel-room.component.html',
  styleUrl: './hotel-room.component.scss',
})
export class HotelRoomComponent implements OnDestroy {

  @Input({ required: true }) rooms: any[] = [];
  mobileView = false;
  @Output() book = new EventEmitter<any>();
  @Output() viewMore = new EventEmitter<any>();
  expandedAmenities: { [roomId: string]: boolean } = {};
  constructor(
    private bp: BreakpointObserver) { }

  ngOnInit() {
    this.bp.observe([Breakpoints.Handset, Breakpoints.TabletPortrait])
      .subscribe(res => this.mobileView = res.matches);
  }

  getRemainingAmenities(amenities: any[] = []): string[] {
    if (!amenities || amenities.length <= 4) return [];
    return amenities.slice(4).map((a) => a?.name || '');
  }

  onBook(room: any, event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.book.emit({ room, event });
  }

  // -------------------------
  // LightGallery
  // -------------------------
  @ViewChild('dynamicGallery', { static: false })
  dynamicGalleryRef!: ElementRef<HTMLElement>;

  private lgInstance: any;

  ngOnDestroy(): void {
    this.destroyGallery();
  }

  onOpenGallery(room: any, startIndex = 0, ev?: Event) {
    ev?.preventDefault();
    ev?.stopPropagation();

    if (!this.dynamicGalleryRef?.nativeElement) return;

    const banners = room?.banners || [];
    if (!banners?.length) return;

    const dynamicEl = this.buildDynamicElFromBanners(banners);
    if (!dynamicEl.length) return;

    const safeIndex = Math.max(0, Math.min(startIndex, dynamicEl.length - 1));

    this.destroyGallery();

    this.lgInstance = lightGallery(this.dynamicGalleryRef.nativeElement, {
      dynamic: true,
      dynamicEl: dynamicEl as any,
      plugins: [lgThumbnail, lgZoom],
      download: false,
      thumbnail: true,
      speed: 400,
      mobileSettings: { controls: true, showCloseIcon: true },
    });

    setTimeout(() => this.lgInstance.openGallery(safeIndex), 0);
  }

  private destroyGallery() {
    if (this.lgInstance?.destroy) {
      this.lgInstance.destroy(true);
      this.lgInstance = null;
    }
  }

  private buildDynamicElFromBanners(banners: any[]) {
    return (banners || [])
      .filter((b: any) => !!b?.imageUrl)
      .map((b: any) => ({
        src: b.imageUrl,
        thumb: b.imageUrl,
      }));
  }

  // -------------------------
  // UI Helpers
  // -------------------------

  getAmenities(room: any): any[] {
    const list = room?.amenityDescriptions || [];
    return list.slice(0, 10);
  }

  amenitiesLeft(room: any): any[] {
    return this.getAmenities(room).slice(0, 7);
  }

  amenitiesRight(room: any): any[] {
    return this.getAmenities(room).slice(7, 14);
  }

  hasThumbs(room: any): boolean {
    return (room?.banners?.length || 0) > 1;
  }

  thumbs(room: any): any[] {
    const total = room?.banners?.length || 0;

    // If view more will be shown, keep only 2 thumbs
    // so total items below hero remain 3
    return total > 4
      ? (room?.banners || []).slice(1, 3)
      : (room?.banners || []).slice(1, 4);
  }

  showViewMoreThumb(room: any): boolean {
    return (room?.banners?.length || 0) > 4;
  }

  viewMoreBg(room: any): string {
    const b = room?.banners || [];
    const url =
      b?.[3]?.imageUrl ||
      b?.[2]?.imageUrl ||
      b?.[1]?.imageUrl ||
      b?.[0]?.imageUrl;

    return url ? `url('${url}')` : 'none';
  }


  maxOccupancy(room: any): { adults: number; children: number } {
  return {
    adults: this.getAdultCount(room) || 0,
    children: this.getChildrenCount(room) || 0,
  };
}


  getVisibleAmenities(room: any): any[] {
  const list = this.getAmenities(room);
  if (!list?.length) return [];

  if (!this.mobileView) return list;
  const isExpanded = this.expandedAmenities[room?.id || room?._id];
  return isExpanded ? list : list.slice(0, 5);
}

  getRemainingBannerCount(room: any): number {
    const total = room?.banners?.length || 0;
    return total > 4 ? total - 4 : 0;
  }

  getAdultCount(room: any): number {
  return Number(
    room?.maxAdults ??
    room?.maxOccupancy?.adults ??
    room?.occupancy?.adults ??
    0
  );
}

getChildrenCount(room: any): number {
  return Number(
    room?.maxChildren ??
    room?.maxOccupancy?.children ??
    room?.occupancy?.children ??
    0
  );
}

}
